import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

/**
 * تبدیل تاریخ میلادی به شمسی
 */
const convertToJalali = (dateString) => {
  if (!dateString) return '';
  
  const date = new Date(dateString);
  const year = date.getFullYear();
  const month = date.getMonth() + 1;
  const day = date.getDate();
  
  // تبدیل ساده به شمسی (تقریبی)
  const jalaliYear = year - 621;
  const jalaliMonth = month > 3 ? month - 3 : month + 9;
  const jalaliDay = day;
  
  return `${jalaliYear}/${jalaliMonth.toString().padStart(2, '0')}/${jalaliDay.toString().padStart(2, '0')}`;
};

/**
 * تبدیل تاریخ و زمان میلادی به شمسی
 */
const convertToJalaliDateTime = (dateString) => {
  if (!dateString) return '';
  
  const date = new Date(dateString);
  const year = date.getFullYear();
  const month = date.getMonth() + 1;
  const day = date.getDate();
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  
  // تبدیل ساده به شمسی (تقریبی)
  const jalaliYear = year - 621;
  const jalaliMonth = month > 3 ? month - 3 : month + 9;
  const jalaliDay = day;
  
  return `${jalaliYear}/${jalaliMonth.toString().padStart(2, '0')}/${jalaliDay.toString().padStart(2, '0')} ${hours}:${minutes}`;
};

/**
 * تبدیل وضعیت ملاقات به فارسی
 */
const getStatusText = (status) => {
  const statusMap = {
    'scheduled': 'برنامه‌ریزی شده',
    'completed': 'تکمیل شده',
    'cancelled': 'لغو شده',
    'no-show': 'عدم حضور'
  };
  return statusMap[status] || status;
};

/**
 * تبدیل نوع ملاقات به فارسی
 */
const getMeetingTypeText = (type) => {
  const typeMap = {
    'in-person': 'حضوری',
    'online': 'آنلاین',
    'phone': 'تلفنی'
  };
  return typeMap[type] || type;
};

/**
 * تبدیل وضعیت پرداخت به فارسی
 */
const getPaymentStatusText = (status) => {
  const statusMap = {
    'pending': 'در انتظار',
    'paid': 'پرداخت شده',
    'partial': 'نیمه پرداخت',
    'cancelled': 'لغو شده'
  };
  return statusMap[status] || status;
};

/**
 * Export لیست مراجعین به اکسل
 */
export const exportClientsToExcel = (clients) => {
  const worksheet = XLSX.utils.json_to_sheet(
    clients.map(client => ({
      'نام و نام خانوادگی': `${client.firstName || ''} ${client.lastName || ''}`.trim(),
      'کد ملی': client.nationalId || '',
      'شماره پرونده': client.fileNumber || '',
      'شماره تماس': client.phone || '',
      'ایمیل': client.email || '',
      'آدرس': client.address || '',
      'شغل': client.occupation || '',
      'تاریخ تولد': convertToJalali(client.birthDate),
      'تماس اضطراری': client.emergencyContact || '',
      'شماره تماس اضطراری': client.emergencyPhone || '',
      'یادداشت': client.notes || '',
      'مشاور': client.consultant?.name || '',
      'وضعیت': client.active !== false ? 'فعال' : 'غیرفعال',
      'تاریخ ثبت': convertToJalali(client.createdAt),
      'آخرین مراجعه': convertToJalali(client.lastVisit)
    }))
  );

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'مراجعین');
  
  // تنظیم عرض ستون‌ها
  const columnWidths = [
    { wch: 25 }, // نام و نام خانوادگی
    { wch: 15 }, // کد ملی
    { wch: 15 }, // شماره پرونده
    { wch: 15 }, // شماره تماس
    { wch: 25 }, // ایمیل
    { wch: 30 }, // آدرس
    { wch: 20 }, // شغل
    { wch: 12 }, // تاریخ تولد
    { wch: 20 }, // تماس اضطراری
    { wch: 15 }, // شماره تماس اضطراری
    { wch: 30 }, // یادداشت
    { wch: 20 }, // مشاور
    { wch: 10 }, // وضعیت
    { wch: 12 }, // تاریخ ثبت
    { wch: 12 }  // آخرین مراجعه
  ];
  worksheet['!cols'] = columnWidths;

  const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
  const data = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  saveAs(data, `مراجعین_${new Date().toLocaleDateString('fa-IR')}.xlsx`);
};

/**
 * Export لیست ملاقات‌ها به اکسل
 */
export const exportAppointmentsToExcel = (appointments) => {
  const worksheet = XLSX.utils.json_to_sheet(
    appointments.map(appointment => ({
      'شماره ملاقات': appointment._id || '',
      'نام مراجع': `${appointment.client?.firstName || ''} ${appointment.client?.lastName || ''}`.trim(),
      'کد ملی مراجع': appointment.client?.nationalId || '',
      'شماره پرونده': appointment.fileNumber || '',
      'نام مشاور': appointment.consultant?.name || '',
      'تاریخ': convertToJalali(appointment.date),
      'ساعت': appointment.time || '',
      'مدت زمان (دقیقه)': appointment.duration || '',
      'نوع ملاقات': getMeetingTypeText(appointment.meetingType),
      'وضعیت': getStatusText(appointment.status),
      'هزینه (تومان)': appointment.cost || 0,
      'وضعیت پرداخت': getPaymentStatusText(appointment.paymentStatus),
      'مبلغ پرداخت شده (تومان)': appointment.paymentAmount || 0,
      'نام همراه': appointment.companionName || '',
      'نسبت همراه': appointment.companionRelation || '',
      'یادداشت': appointment.notes || '',
      'تاریخ ثبت': convertToJalaliDateTime(appointment.createdAt),
      'تاریخ بروزرسانی': convertToJalaliDateTime(appointment.updatedAt)
    }))
  );

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'ملاقات‌ها');
  
  // تنظیم عرض ستون‌ها
  const columnWidths = [
    { wch: 25 }, // شماره ملاقات
    { wch: 25 }, // نام مراجع
    { wch: 15 }, // کد ملی مراجع
    { wch: 15 }, // شماره پرونده
    { wch: 20 }, // نام مشاور
    { wch: 12 }, // تاریخ
    { wch: 10 }, // ساعت
    { wch: 15 }, // مدت زمان
    { wch: 12 }, // نوع ملاقات
    { wch: 15 }, // وضعیت
    { wch: 15 }, // هزینه
    { wch: 15 }, // وضعیت پرداخت
    { wch: 20 }, // مبلغ پرداخت شده
    { wch: 20 }, // نام همراه
    { wch: 15 }, // نسبت همراه
    { wch: 30 }, // یادداشت
    { wch: 20 }, // تاریخ ثبت
    { wch: 20 }  // تاریخ بروزرسانی
  ];
  worksheet['!cols'] = columnWidths;

  const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
  const data = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  saveAs(data, `ملاقات‌ها_${new Date().toLocaleDateString('fa-IR')}.xlsx`);
};

/**
 * Export پاسخ‌های یک فرم به اکسل
 * @param {string} formTitle - عنوان فرم
 * @param {Array<{key: string, label?: string}>} fieldKeys - ستون‌های فیلدها
 * @param {Array} submissions - پاسخ‌های ثبت‌شده
 */
export const exportFormSubmissionsToExcel = (formTitle, fieldKeys, submissions) => {
  const safeTitle = (formTitle || 'فرم')
    .replace(/[/\\?%*:|"<>]/g, '_')
    .slice(0, 40);

  const worksheet = XLSX.utils.json_to_sheet(
    submissions.map((s) => {
      const row = {
        'شماره موبایل (تأییدشده)': s.phone || '',
      };
      (fieldKeys || []).forEach((f) => {
        const col = f.label || f.key;
        row[col] = s.answers?.[f.key] != null ? String(s.answers[f.key]) : '';
      });
      row['تاریخ ثبت'] = convertToJalaliDateTime(s.createdAt);
      row['آخرین به‌روزرسانی'] = convertToJalaliDateTime(s.updatedAt);
      return row;
    })
  );

  const workbook = XLSX.utils.book_new();
  const sheetName = safeTitle.slice(0, 31) || 'پاسخ‌ها';
  XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

  const columnWidths = [
    { wch: 18 },
    ...(fieldKeys || []).map(() => ({ wch: 22 })),
    { wch: 20 },
    { wch: 20 },
  ];
  worksheet['!cols'] = columnWidths;

  const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
  const data = new Blob([excelBuffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  saveAs(data, `پاسخ‌های_${safeTitle}_${new Date().toLocaleDateString('fa-IR')}.xlsx`);
};
